import axios from 'axios';
import { DiscountDto } from './discountsService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Enums
export enum PaymentMethod {
  Cash = 1,
  BankTransfer = 2,
  Check = 3,
  CreditCard = 4,
  DirectDebit = 5,
  Other = 6
}

export enum PaymentStatus {
  Pending = 1,
  Completed = 2,
  Cancelled = 3,
  Failed = 4
}

export enum PaymentPlanStatus {
  Active = 1,
  Completed = 2,
  Cancelled = 3
}

export enum PaymentPlanSetupMethod {
  Manual = 1,
  Cardcom = 2
}

// Types
export interface PaymentDto {
  id: number;
  clientId: number;
  clientName: string;
  paymentDate: string;
  amount: number;
  paymentMethod: number;
  paymentMethodName: string;
  status: number;
  statusName: string;
  description?: string;
  referenceNumber?: string;
  paymentPlanId?: number;
  failureReason?: string;
  recordedBy: number;
  recordedByName: string;
  createdAt: string;
  updatedAt?: string;
  planNotice?: PaymentPlanNoticeDto | null; // שינוי בתוכנית התשלומים (הושלמה/בוטלה) — רק ברישום תשלום מחוץ לתוכנית
}

export interface PaymentPlanNoticeDto {
  text: string;
  isWarning: boolean; // נדרשת פעולה (התוכנית בוטלה / העדכון נכשל)
}

export interface CreatePaymentDto {
  clientId: number;
  paymentDate: string;
  amount: number;
  paymentMethod: number;
  description?: string;
  referenceNumber?: string;
  paymentPlanId?: number;
}

export interface UpdatePaymentDto {
  paymentDate: string;
  amount: number;
  paymentMethod: number;
  status: number;
  description?: string;
  referenceNumber?: string;
}

export interface PaymentInstallmentInfo {
  installmentNumber: number;
  dueDate: string;
  amount: number;
  isPaid: boolean;
  isOverdue?: boolean; // האם התשלום בפיגור (טרם שולם ועבר תאריך היעד) - מחושב בשרת
  paymentId?: number;
  paidDate?: string;
  reminderId?: number;
  isReminderHandled: boolean;
}

export interface PaymentPlanDto {
  id: number;
  clientId: number;
  clientName: string;
  totalAmount: number;
  numberOfInstallments: number;
  installmentAmount: number;
  firstPaymentDate: string;
  status: number;
  statusName: string;
  notes?: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
  paymentsReceived: number;
  amountPaid: number;
  remainingAmount: number;
  nextPaymentDate?: string;
  installments: PaymentInstallmentInfo[];
  setupMethod?: number;
  dayOfMonth?: number;
  cardcomToken?: string;
  cardcomMasterRecurringId?: string;
  lastCardUpdateEmailSentAt?: string;
  // Failure tracking for active recurring charges
  lastChargeFailureMessage?: string;
  lastChargeFailureAt?: string;
  lastChargeFailureAttempt?: number;
  lastChargeFailureInstallmentNumber?: number;
}

export interface UpdatePlanAmountDto {
  amount: number;
}

export interface UpdatePlanDayOfMonthDto {
  dayOfMonth: number;
}

export interface CreatePaymentPlanDto {
  clientId: number;
  totalAmount: number;
  numberOfInstallments: number;
  firstPaymentDate: string;
  notes?: string;
  setupMethod: PaymentPlanSetupMethod;
  monthlyAmount?: number;
  dayOfMonth?: number;
  // הארכת ההנחות החודשיות הפעילות עד התשלום האחרון בתוכנית ("הארך את ההנחה החודשית עד סוף התוכנית")
  extendMonthlyDiscountsToPlanEnd?: boolean;
}

export interface PaymentPlanCardcomFields {
  setupMethod: PaymentPlanSetupMethod;
  cardcomMasterRecurringId?: string;
  cardcomToken?: string;
  dayOfMonth?: number;
}

export interface CardcomIframeUrlDto {
  iframeUrl: string;
}

export interface CreateRecurringIframeRequestDto {
  monthlyAmount: number;
  dayOfMonth: number;
}

export interface InitiateOneTimeChargeDto {
  clientId: number;
  amount: number;
  description?: string;
}

export interface CardcomOneTimeInitiateResultDto {
  paymentId: number;
  iframeUrl: string;
  lowProfileId: string;
}

export interface OneTimeChargeStatusDto {
  status: 'Pending' | 'Completed' | 'Failed' | 'Cancelled';
  failureReason?: string;
}

export interface UpdatePaymentPlanDto {
  status: number;
  notes?: string;
}

export interface ConfirmPaymentPlanInstallmentDto {
  paymentPlanId: number;
  installmentNumber: number;
  paymentDate: string;
  amount: number;
  paymentMethod: number;
  referenceNumber?: string;
  notes?: string;
}

export interface BillingDto {
  id: number;
  clientId: number;
  clientName: string;
  committeeId?: number;
  monthlyBenefitAmount?: number;
  numberOfMonths?: number;
  manualNumberOfMonths?: number | null;
  totalBenefits?: number;
  retroactiveTotalAmount?: number;
  retroactiveFromDate?: string;
  commissionPercentage: number;
  commissionOnBenefits?: number;
  commissionOnRetroactive?: number;
  totalCharge: number;
  totalPaid: number;
  balance: number;
  notes?: string;
  createdBy: number;
  createdByName: string;
  updatedBy?: number;
  updatedByName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface BillingBreakdownDto {
  monthlyBenefitAmount?: number;
  numberOfMonths?: number;
  manualNumberOfMonths?: number | null;
  totalBenefits?: number;
  commissionPercentage: number;
  commissionOnBenefits?: number;
  retroactiveTotalAmount?: number;
  retroactiveFromDate?: string;
  retroactiveMonths?: number;
  commissionOnRetroactive?: number;
  manualChargesTotal: number; // סך החיובים הידניים, נטו (כלול ב-totalCharge)
  hasCommission: boolean; // יש עמלת ועדה; false = חיוב ידני בלבד, לפני ועדה
  totalCharge: number; // עמלה + חיובים ידניים, נטו (ללא מע"מ)
  vatRate: number; // אחוז המע"מ
  vatAmount: number; // תוספת המע"מ
  totalWithVat: number; // סה"כ כולל מע"מ
  committeeId?: number;
  notes?: string;
}

// חיוב ידני ללקוח — מצטרף לעמלה. הסכום נטו, לפני מע"מ.
export interface ManualChargeDto {
  id: number;
  clientId: number;
  amount: number;
  chargeDate: string;
  description: string;
  createdAt: string;
}

export interface CreateManualChargeDto {
  clientId: number;
  amount: number;
  chargeDate: string;
  description: string;
}

export interface UpdateManualChargeDto {
  amount: number;
  chargeDate: string;
  description: string;
}

export interface BillingSummaryDto {
  clientId: number;
  clientName: string;
  totalCharge: number; // עמלה + חיובים ידניים, נטו (ללא מע"מ)
  vatRate: number; // אחוז המע"מ
  vatAmount: number; // תוספת המע"מ
  totalWithVat: number; // סה"כ לתשלום כולל מע"מ
  totalPaid: number;
  balance: number; // יתרה כולל מע"מ (לפני הנחה)
  // ⚠️ שתי סקאלות שונות במכוון: ההנחה מוצגת נטו כפי שהוזנה, אך מנוכה מהיתרה בברוטו
  // (בתוספת המע"מ שלא נגבה). לכן totalWithVat − totalPaid − totalDiscount ≠ balanceAfterDiscount.
  totalDiscount: number; // סך ההנחה האפקטיבית — נטו, לפני מע"מ
  balanceAfterDiscount: number; // יתרה לתשלום לאחר ניכוי ההנחה כולל המע"מ שלה
  discounts: DiscountDto[]; // ההנחות של הלקוח (לשורות זיכוי בהיסטוריה)
  manualCharges: ManualChargeDto[]; // חיובים ידניים — כלולים ב-totalCharge (מהחדש לישן)
  breakdown?: BillingBreakdownDto;
  payments: PaymentDto[];
  paymentPlan?: PaymentPlanDto;
  previousPaymentPlans: PaymentPlanDto[]; // תוכניות קודמות (מבוטלות/הושלמו)
  billingRecord?: BillingDto;
}

// P9a — פירוט העמלות החבות לסוכן/ארגון של הלקוח (חישוב בלבד).
// עמלת חתימה חבה תמיד; עמלת אישור (nullable) קיימת אך ורק כשהקצבה אושרה (isAllowanceApproved).
export interface ClientCommissionOwedDto {
  clientId: number;
  isAllowanceApproved: boolean;
  approvalBaseNet?: number | null; // עמלת הוועדה בלבד (נטו, ללא מע"מ, בלי חיובים ידניים); null כשלא אושר

  agentSourceId?: number | null;
  agentSourceName?: string | null;
  agentSigningCommissionAmount?: number | null;
  agentApprovalCommissionPercentage?: number | null;
  agentSigningOwed?: number | null;
  agentApprovalOwed?: number | null; // null = אינו קיים (לא אושר / אין אחוז)
  agentTotalOwed: number;

  organizationSourceId?: number | null;
  organizationSourceName?: string | null;
  organizationSigningCommissionAmount?: number | null;
  organizationApprovalCommissionPercentage?: number | null;
  organizationSigningOwed?: number | null;
  organizationApprovalOwed?: number | null;
  organizationTotalOwed: number;

  totalOwed: number;
}

export interface CreateBillingDto {
  clientId: number;
  committeeId?: number;
  monthlyBenefitAmount?: number;
  numberOfMonths?: number;
  retroactiveTotalAmount?: number;
  commissionPercentage?: number;
  notes?: string;
}

export interface UpdateBillingDto {
  monthlyBenefitAmount?: number;
  numberOfMonths?: number;
  retroactiveTotalAmount?: number;
  commissionPercentage: number;
  notes?: string;
}

// תוצאה של חישוב חיוב מחדש — כולל מה קרה לתוכנית התשלומים הפעילה.
// תוכנית פעילה מותאמת לסכום החדש ואינה מבוטלת, אלא אם לא נותרה יתרה לגבות.
/**
 * סף עיגול לסיווג "שולם במלואו" ולהחלטות — תואם ClientBalanceCalculator.Tolerance בשרת.
 * יתרה של אגורות (עיגול של תוכניות ישנות) אינה "יתרה לתשלום".
 */
export const BALANCE_TOLERANCE = 1;

// תואם BillingRecalcTrigger בשרת — מה גרם לחישוב/ליישור התוכנית
export enum BillingRecalcTrigger {
  CommitteeDecision = 1,
  ManualMonths = 2,
  ManualCommission = 3,
  ManualCharge = 4,
  Discount = 5, // שינוי בהנחה — החיוב לא משתנה, רק התוכנית מתיישרת ליתרה
}

export interface BillingUpdateResultDto {
  billing: BillingDto;
  trigger?: BillingRecalcTrigger;
  paymentPlanCancelled: boolean;
  totalPaidInPlan?: number | null;
  remainingBalance?: number | null;
  paymentPlanAdjusted: boolean;
  oldInstallmentAmount?: number | null;
  newInstallmentAmount?: number | null;
  warning?: string | null;
}

// API - Billing
export const billingApi = {
  getByClientId: async (clientId: number): Promise<BillingDto | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payments/billing/client/${clientId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getSummary: async (clientId: number): Promise<BillingSummaryDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/billing/summary/${clientId}`);
    return response.data;
  },

  // P9a — פירוט העמלות החבות לסוכן/ארגון (מגודר CanViewClientFinance בשרת).
  getCommissionOwed: async (clientId: number): Promise<ClientCommissionOwedDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/billing/commission-owed/${clientId}`);
    return response.data;
  },

  create: async (dto: CreateBillingDto): Promise<BillingDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/billing`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateBillingDto): Promise<BillingDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/billing/${id}`, dto);
    return response.data;
  },

  // עדכון ידני של מספר חודשי העמלה (עתיד). null = איפוס לחישוב האוטומטי.
  updateMonths: async (id: number, manualNumberOfMonths: number | null): Promise<BillingUpdateResultDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/billing/${id}/months`, { manualNumberOfMonths });
    return response.data;
  },

  // עדכון אחוז העמלה וחישוב מחדש של הסכום לתשלום.
  // האחוז נשמר על הלקוח וגובר על אחוז החוזה; החוזה החתום עצמו אינו משתנה.
  updateCommission: async (id: number, commissionPercentage: number): Promise<BillingUpdateResultDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/billing/${id}/commission`, { commissionPercentage });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/payments/billing/${id}`);
  }
};

// API - Manual charges (חיובים ידניים). כל שינוי מחשב מחדש את החיוב ומחזיר מה קרה לתוכנית התשלומים.
export const manualChargesApi = {
  create: async (dto: CreateManualChargeDto): Promise<BillingUpdateResultDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/manual-charges`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateManualChargeDto): Promise<BillingUpdateResultDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/manual-charges/${id}`, dto);
    return response.data;
  },

  // null = רשומת החיוב נמחקה יחד עם החיוב האחרון (204) — הלקוח חוזר למצב "אין חיוב"
  remove: async (id: number): Promise<BillingUpdateResultDto | null> => {
    const response = await axios.delete(`${API_BASE_URL}/payments/manual-charges/${id}`);
    return response.status === 204 ? null : response.data;
  }
};

// API - Payments
export const paymentsApi = {
  getById: async (id: number): Promise<PaymentDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/${id}`);
    return response.data;
  },

  getByClientId: async (clientId: number): Promise<PaymentDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/payments/client/${clientId}`);
    return response.data;
  },

  create: async (dto: CreatePaymentDto): Promise<PaymentDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdatePaymentDto): Promise<PaymentDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/${id}`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/payments/${id}`);
  },

  initiateOneTimeCharge: async (dto: InitiateOneTimeChargeDto): Promise<CardcomOneTimeInitiateResultDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/cardcom/one-time/initiate`, dto);
    return response.data;
  },

  getOneTimeChargeStatus: async (paymentId: number): Promise<OneTimeChargeStatusDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/cardcom/one-time/${paymentId}/status`);
    return response.data;
  }
};

// API - Payment Plans
export const paymentPlansApi = {
  getById: async (id: number): Promise<PaymentPlanDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/plans/${id}`);
    return response.data;
  },

  getActiveByClientId: async (clientId: number): Promise<PaymentPlanDto | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payments/plans/client/${clientId}/active`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getByClientId: async (clientId: number): Promise<PaymentPlanDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/payments/plans/client/${clientId}`);
    return response.data;
  },

  create: async (dto: CreatePaymentPlanDto): Promise<PaymentPlanDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/plans`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdatePaymentPlanDto): Promise<PaymentPlanDto> => {
    const response = await axios.put(`${API_BASE_URL}/payments/plans/${id}`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/payments/plans/${id}`);
  },

  confirmInstallment: async (dto: ConfirmPaymentPlanInstallmentDto): Promise<PaymentDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/plans/${dto.paymentPlanId}/confirm-installment`, dto);
    return response.data;
  },

  getCardcomIframeUrl: async (planId: number, dto: CreateRecurringIframeRequestDto): Promise<CardcomIframeUrlDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/plans/${planId}/recurring/iframe-url`, dto);
    return response.data;
  },

  cancel: async (id: number): Promise<PaymentPlanDto> => {
    const response = await axios.post(`${API_BASE_URL}/payments/plans/${id}/cancel`);
    return response.data;
  },

  sendCardUpdateEmail: async (id: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/payments/plans/${id}/send-card-update-email`);
  },

  updateAmount: async (id: number, amount: number): Promise<PaymentPlanDto> => {
    const response = await axios.patch(`${API_BASE_URL}/payments/plans/${id}/amount`, { amount });
    return response.data;
  },

  updateDayOfMonth: async (id: number, dayOfMonth: number): Promise<PaymentPlanDto> => {
    const response = await axios.patch(`${API_BASE_URL}/payments/plans/${id}/day-of-month`, { dayOfMonth });
    return response.data;
  },

  getCardUpdateUrl: async (id: number): Promise<CardcomIframeUrlDto> => {
    const response = await axios.get(`${API_BASE_URL}/payments/plans/${id}/card-update-url`);
    return response.data;
  }
};

// Helper functions
export const getPaymentMethodName = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.Cash: return 'מזומן';
    case PaymentMethod.BankTransfer: return 'העברה בנקאית';
    case PaymentMethod.Check: return 'צ\'ק';
    case PaymentMethod.CreditCard: return 'כרטיס אשראי';
    case PaymentMethod.DirectDebit: return 'הוראת קבע';
    case PaymentMethod.Other: return 'אחר';
    default: return 'לא ידוע';
  }
};

export const getPaymentStatusName = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.Pending: return 'ממתין';
    case PaymentStatus.Completed: return 'הושלם';
    case PaymentStatus.Cancelled: return 'בוטל';
    case PaymentStatus.Failed: return 'נכשל';
    default: return 'לא ידוע';
  }
};

export const getPaymentStatusColor = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.Pending: return 'var(--warning-color)';
    case PaymentStatus.Completed: return 'var(--success-color)';
    case PaymentStatus.Cancelled: return 'var(--text-secondary)';
    case PaymentStatus.Failed: return 'var(--error-color)';
    default: return 'var(--text-secondary)';
  }
};

export const getPaymentPlanStatusName = (status: PaymentPlanStatus): string => {
  switch (status) {
    case PaymentPlanStatus.Active: return 'פעיל';
    case PaymentPlanStatus.Completed: return 'הושלם';
    case PaymentPlanStatus.Cancelled: return 'בוטל';
    default: return 'לא ידוע';
  }
};

export const getPaymentPlanStatusColor = (status: PaymentPlanStatus): string => {
  switch (status) {
    case PaymentPlanStatus.Active: return 'var(--primary-color)';
    case PaymentPlanStatus.Completed: return 'var(--success-color)';
    case PaymentPlanStatus.Cancelled: return 'var(--text-secondary)';
    default: return 'var(--text-secondary)';
  }
};

export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';
